import { useCallback, useEffect, useMemo, useState } from 'react';
import { doMeiliSearch } from '../../music24/components/SearchField/searchService';
import { MeiliIndex, MeiliTrackData } from '../../music24/components/SearchField/types';

export interface SongSearch {
  /** The songs of the artist first, then the rest. */
  own: MeiliTrackData[];
  others: MeiliTrackData[];
  isSearching: boolean;
  /** The search answered and found nothing. */
  isEmpty: boolean;
  /**
   * The search did not answer (an error, the search service down). Never shown as "No song
   * found": that tells an artist that a released song does not exist (QA 2026-10-28, mob-10).
   */
  isFailed: boolean;
  /** Asks the search again after a failure. */
  retry: () => void;
}

const MIN_QUERY_LENGTH = 2;

function sameName(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * The catalog search of "It is out" (design c1): the same Meilisearch index as the claim search
 * of the Catalog. `query` is the debounced text; a link is not searched (pass an empty string).
 * The results list the songs of the artist first.
 */
export function useSongSearch(query: string, artistName: string): SongSearch {
  const [hits, setHits] = useState<MeiliTrackData[] | undefined>();
  const [answeredFor, setAnsweredFor] = useState('');
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const text = query.trim();
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (text.length < MIN_QUERY_LENGTH) {
      setHits(undefined);
      setAnsweredFor('');
      return undefined;
    }
    let isCancelled = false;
    setFailedFor(null);
    setAnsweredFor('');
    const fail = (error?: unknown) => {
      if (!isCancelled) {
        console.error('Song search failed', error);
        setHits([]);
        setFailedFor(text);
        setAnsweredFor(text);
      }
    };
    doMeiliSearch(text, [MeiliIndex.tracks])
      .then((response) => {
        if (isCancelled) {
          return;
        }
        // `doMeiliSearch` answers null for a failed search (it logs the error itself).
        if (response === null) {
          fail();
          return;
        }
        const tracks = (response[0]?.items ?? [])
          .filter((item) => item.type === MeiliIndex.tracks)
          .map((item) => item.item as MeiliTrackData);
        setHits(tracks);
        setAnsweredFor(text);
      })
      .catch(fail);
    return () => {
      isCancelled = true;
    };
  }, [text, attempt]);

  return useMemo(() => {
    const all = hits ?? [];
    const own = artistName === '' ? [] : all.filter((track) => sameName(track.artistName, artistName));
    const others = all.filter((track) => !own.includes(track));
    const isSearching = text.length >= MIN_QUERY_LENGTH && answeredFor !== text;
    const isFailed = !isSearching && failedFor === text;
    return {
      own,
      others,
      isSearching,
      isEmpty: !isSearching && !isFailed && hits !== undefined && all.length === 0,
      isFailed,
      retry,
    };
  }, [answeredFor, artistName, failedFor, hits, retry, text]);
}
